import { Company } from './company';

interface ProfileView {
	id: string;
	admin: boolean;
	created_at: string;
	custom_properties: Record<string, string | null>;
	display_name: string;
	email: string;
	first_name: string;
	full_pic_url: string;
	has_manager: boolean;
	is_manager: boolean;
	last_name: string;
	locale: string;
	profile_pic_url: string;
	slug: string;
	status: string;
	super_admin: boolean;
	updated_at: string;
	user_mode: string;
	username: string;
}

interface NavView {
	company_admin: boolean;
	earning_balance: number;
	earning_balance_with_currency: string;
	finance_admin: boolean;
	giving_balance: number;
	global_admin: boolean;
	rewards_admin: boolean;
	show_awards: boolean;
	show_integrations: boolean;
	tech_admin: boolean;
}

export interface UserProfile extends ProfileView, NavView {}

export enum UserMode {
	Normal = 'normal',
	Benefactor = 'benefactor',
	Receiver = 'receiver',
	Observer = 'observer',
}

export type User = {
	id: string;
	admin: boolean;
	can_give: boolean;
	can_give_non_zero: boolean;
	can_receive: boolean;
	company: Company;
	company_admin: boolean;
	created_at: string;
	hired_on?: string | null;
	custom_properties: Record<string, string | null>;
	custom_feeds: CustomFeed[];
	direct_report_ids: string[];
	display_name: string;
	earning_balance: number;
	earning_balance_with_currency: string;
	email: string;
	preferred_first_name?: string;
	feed_preference: string;
	first_name: string;
	following?: Array<string>;
	full_pic_url: string;
	giving_balance: number;
	giving_balance_with_currency: string;
	has_manager: boolean;
	is_manager: boolean;
	last_name: string;
	locale: string;
	monthly_allowance: number;
	ppp_adjustment: number;
	profile_pic_url: string;
	redemption_rules?: RedemptionRule;
	show_awards: boolean;
	show_integrations: boolean;
	slug: string;
	status: string;
	suggested_give_amounts?: SuggestedGiveAmount[];
	user_mode: UserMode;
	username: string;
	all_usernames: string[];
};

export type SuggestedGiveAmount = {
	dataValue: number;
	name: string;
};

export type CustomFeed = {
	filter: string;
	option: {
		title: string;
		value: string;
	};
};

export type RedemptionRule = {
	message: string;
	redemption_allowed: boolean;
};

// Direct Report types
export type DirectReportAPIData = {
	display_name: string;
	email: string;
	id: string;
	last_recognition_at: number;
	profile_pic_url: string;
	recognition_count: number;
	username: string;
	deleted_at: string | null;
	has_upcoming_one_on_one: boolean;
};

export type DirectReport = {
	id: string;
	displayName: string;
	email: string;
	lastRecognitionAt: Date;
	profilePic: string;
	recognitionCount: number;
	username: string;
	deletedAt: string | null;
	hasUpcomingOneOnOne: boolean;
};

export const transformDirectReport = (data: DirectReportAPIData): DirectReport => ({
	id: data.id,
	displayName: data.display_name,
	email: data.email,
	lastRecognitionAt: new Date(data.last_recognition_at),
	profilePic: data.profile_pic_url,
	recognitionCount: data.recognition_count,
	username: data.username,
	deletedAt: data.deleted_at,
	hasUpcomingOneOnOne: data.has_upcoming_one_on_one,
});

// User Detail types
export type UserDetailAPIData = {
	id: string;
	custom_properties: Record<string, string>;
	display_name: string;
	email: string;
	first_name: string;
	display_first_name: string;
	last_name: string;
	locale: string;
	profile_pic: string;
	time_zone: string | null;
	user_mode: string;
	manager_id: string | null;
	username: string;
	deleted_at: string | null;
	status: string;
	created_at: string;
	updated_at: string;
};

export type UserDetail = {
	id: string;
	customProperties: Record<string, string>;
	displayName: string;
	email: string;
	firstName: string;
	displayFirstName: string;
	lastName: string;
	locale: string;
	profilePic: string;
	timeZone: string | null;
	userMode: string;
	managerId: string | null;
	username: string;
	deletedAt: string | null;
	status: string;
	createdAt: Date;
	updatedAt: Date;
};

export const transformUserDetail = (data: UserDetailAPIData): UserDetail => ({
	id: data.id,
	customProperties: data.custom_properties,
	displayName: data.display_name,
	email: data.email,
	firstName: data.first_name,
	displayFirstName: data.display_first_name,
	lastName: data.last_name,
	locale: data.locale,
	profilePic: data.profile_pic,
	timeZone: data.time_zone,
	userMode: data.user_mode,
	managerId: data.manager_id,
	username: data.username,
	deletedAt: data.deleted_at,
	status: data.status,
	createdAt: new Date(data.created_at),
	updatedAt: new Date(data.updated_at),
});
